import menuArrowIcon from "@assets/images/menu-arrow-icon.svg";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Grow from "@material-ui/core/Grow";
import Hidden from "@material-ui/core/Hidden";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import { accountSettingDetailsUrl } from "@saleor/accountSettings/urls";
import { chooseAccountPath, userLoginPath } from "@saleor/auth/urls";
import { createConfigurationMenu } from "@saleor/configuration";
import useAppState from "@saleor/hooks/useAppState";
import useLocalStorage from "@saleor/hooks/useLocalStorage";
import useNavigator from "@saleor/hooks/useNavigator";
import useTheme from "@saleor/hooks/useTheme";
import useUser from "@saleor/hooks/useUser";
import ArrowDropdown from "@saleor/icons/ArrowDropdown";
import SaveAccountDialog from "@saleor/navigation/components/SaveAccountDialog/SaveAccountDialog";
import { userBusinessDetailsPath } from "@saleor/staff/urls";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import Container from "../Container";
import ErrorPage from "../ErrorPage";
import AccountMenu from "./AccountMenu";
import AppActionContext from "./AppActionContext";
import AppHeaderContext from "./AppHeaderContext";
import { appLoaderHeight, drawerWidth, drawerWidthExpanded } from "./consts";
import MenuList from "./MenuList";
import createMenuStructure from "./menuStructure";
import ResponsiveDrawer from "./ResponsiveDrawer";
import { useSavedAccount } from "./SavedAccountContext";
const useStyles = makeStyles(
  theme => ({
    appAction: {
      [theme.breakpoints.down("sm")]: {
        left: 0,
        width: "100%"
      },
      bottom: 0,
      gridColumn: 2,
      position: "sticky",
      zIndex: 10
    },
    appActionDocked: {
      position: "static"
    },
    appLoader: {
      height: appLoaderHeight,
      marginBottom: theme.spacing(2),
      zIndex: 1199
    },
    appLoaderPlaceholder: {
      height: appLoaderHeight,
      marginBottom: theme.spacing(2)
    },
    arrow: {
      marginLeft: theme.spacing(2),
      transition: theme.transitions.duration.standard + "ms"
    },
    avatar: {
      "&&": {
        height: 32,
        width: 32
      }
    },
    content: {
      [theme.breakpoints.down("sm")]: {
        paddingLeft: 0
      },
      paddingLeft: drawerWidthExpanded,
      paddingTop: "80px",
      transition: "padding-left 0.5s ease",
      width: "100%"
    },
    contentToggle: {
      [theme.breakpoints.down("sm")]: {
        paddingLeft: 0
      },
      paddingLeft: drawerWidth
    },
    darkThemeSwitch: {
      "@media (max-width: 767px)": {
        display: "none"
      },
      [theme.breakpoints.down("sm")]: {
        // marginRight: -theme.spacing(1.5)
        marginRight: 0
      },
      marginRight: theme.spacing(2)
    },
    header: {
      "@media (max-width: 767px)": {
        right: "10px"
      },
      [theme.breakpoints.down("sm")]: {
        height: 88,
        marginBottom: 0
      },
      display: "flex",
      height: 40,
      marginBottom: "50px",
      position: "fixed",
      right: "20px",
      top: "20px",
      zIndex: 999
    },
    isMenuSmall: {
      "& path": {
        fill: theme.palette.primary.main
      },
      "& span": {
        margin: "0 8px"
      },
      "& svg": {
        marginTop: 8,
        transform: "rotate(180deg)"
      },
      "&:hover": {
        background: "#E6F3F3"
      },
      background: theme.palette.background.paper,
      border: `solid 1px #EAEAEA`,
      borderRadius: "50%",
      cursor: "pointer",
      height: 32,
      position: "absolute",
      right: -16,
      top: -16,
      transition: `background ${theme.transitions.duration.shorter}ms`,
      width: 32,
      zIndex: 99
    },
    isMenuSmallDark: {
      "&:hover": {
        background: `linear-gradient(0deg, rgba(25, 195, 190, 0.1), rgba(25, 195, 190, 0.1)), ${theme.palette.background.paper}`
      },
      border: `solid 1px #252728`,
      transition: `background  ${theme.transitions.duration.shorter}ms`
    },
    isMenuSmallHide: {
      "& svg": {
        marginLeft: "3px",
        transform: "rotate(0deg)"
      }
    },
    isToggleButtonHidden: {
      opacity: 0,
      transition: `opacity ${theme.transitions.duration.shorter}ms`
    },
    logo: {
      "& svg": {
        left: "50%",
        position: "absolute",
        top: "50%",
        transform: "translate(-50%,-50%)"
      },
      background: theme.palette.secondary.main,
      display: "block",
      height: 80,
      position: "relative"
    },
    logoDark: {
      "& path": {
        fill: theme.palette.common.white
      },
      background: theme.palette.primary.main
    },
    logoSmall: {
      "& svg": {
        margin: 0,
        padding: 0,
        width: "80px"
      }
    },
    menu: {
      "&::-webkit-scrollbar ": {
        width: 1
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(33, 81, 161, 0.3)",
        outline: "1px solid slategrey"
      },
      "&::-webkit-scrollbar-track": {
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)"
      },
      background: theme.palette.background.paper,
      height: "100vh",
      overflowY: "scroll",
      padding: "25px 20px"
    },
    menuIcon: {
      "& span": {
        "&:nth-child(1)": {
          top: 15
        },
        "&:nth-child(2), &:nth-child(3)": {
          top: 20
        },
        "&:nth-child(4)": {
          top: 25
        },
        background: theme.palette.secondary.light,
        display: "block",
        height: 1,
        left: "20%",
        opacity: 1,
        position: "absolute",
        transform: "rotate(0deg)",
        transition: ".25s ease-in-out",
        width: "60%"
      },
      "@media (max-width: 767px)": {
        left: "inherit !important",
        marginRight: "10px"
      },
      [theme.breakpoints.up("md")]: {
        display: "none"
      },
      [theme.breakpoints.down("sm")]: {
        left: 0
      },
      background: theme.palette.background.paper,
      borderRadius: "50%",
      cursor: "pointer",
      height: 42,
      left: theme.spacing(),
      marginRight: theme.spacing(2),
      position: "relative",
      transform: "rotate(0deg)",
      transition: `${theme.transitions.duration.shorter}ms ease-in-out`,
      width: 42
    },
    menuIconDark: {
      "& span": {
        background: theme.palette.common.white
      }
    },
    menuIconOpen: {
      "& span": {
        "&:nth-child(1), &:nth-child(4)": {
          left: "50%",
          top: 20,
          width: 0
        },
        "&:nth-child(2)": {
          transform: "rotate(45deg)"
        },
        "&:nth-child(3)": {
          transform: "rotate(-45deg)"
        }
      },
      left: 280,
      position: "absolute",
      zIndex: 1999
    },
    menuSmall: {
      "&::-webkit-scrollbar ": {
        width: 1
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(33, 81, 161, 0.3)",
        outline: "1px solid slategrey"
      },
      "&::-webkit-scrollbar-track": {
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)"
      },
      background: theme.palette.background.paper,
      height: "94vh",
      overflowY: "auto",
      padding: 25
    },
    popover: {
      [theme.breakpoints.up("md")]: {
        maxWidth: "30vw"
      },
      zIndex: 99999
    },
    root: {
      width: `100%`
    },
    rotate: {
      transform: "rotate(180deg)"
    },
    sideBar: {
      [theme.breakpoints.down("sm")]: {
        padding: 0
      },
      background: theme.palette.background.paper,
      padding: `0 ${theme.spacing(4)}px`
    },
    spacer: {
      flex: 1
    },
    userBar: {
      [theme.breakpoints.down("sm")]: {
        // alignItems: "flex-end",
        // flexDirection: "column-reverse",
        // overflow: "hidden",
        marginTop: "-40px"
      },
      alignItems: "center",
      display: "flex"
    },
    userChip: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 24,
      color: theme.palette.text.primary,
      height: 40,
      padding: theme.spacing(0.5)
    },
    userChipLabel: {
      alignItems: "center",
      display: "flex"
    },
    userMenuContainer: {
      marginLeft: "10px",
      position: "relative"
    },
    userMenuItem: {
      fontSize: "0.875rem",
      paddingLeft: 10,
      paddingRight: 10,
      textAlign: "right"
    },
    view: {
      backgroundColor: theme.palette.background.default,
      flex: 1,
      flexGrow: 1,
      marginLeft: 0,
      paddingBottom: theme.spacing(),
      [theme.breakpoints.up("sm")]: {
        paddingBottom: theme.spacing(3)
      }
    },
    viewContainer: {
      minHeight: `calc(100vh - ${theme.spacing(2) + appLoaderHeight + 70}px)`
    }
  }),
  {
    name: "AppLayout"
  }
);

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const classes = useStyles({});
  const { isDark } = useTheme();
  const [isMenuSmall, setMenuSmall] = useLocalStorage("isMenuSmall", false);
  const [isDrawerOpened, setDrawerState] = React.useState(false);
  const [isMenuOpened, setMenuState] = React.useState(false);
  const appActionAnchor = React.useRef<HTMLDivElement>();
  const appHeaderAnchor = React.useRef<HTMLDivElement>();
  const anchor = React.useRef<HTMLDivElement>();
  const { logout, user } = useUser();
  const navigate = useNavigator();
  const intl = useIntl();
  const [appState, dispatchAppState] = useAppState();
  const location = useLocation();
  const [docked, setDocked] = React.useState(true);

  const menuStructure = createMenuStructure(intl);
  const configurationMenu = createConfigurationMenu(intl);
  const userPermissions = user?.userPermissions || [];
  const { savedAccounts } = useSavedAccount();
  const renderConfigure = configurationMenu.some(section =>
    section.menuItems.some(
      menuItem =>
        !!userPermissions.find(
          userPermission => userPermission.code === menuItem.permission
        )
    )
  );

  const handleLogout = () => {
    setMenuState(false);
    logout();
    if (savedAccounts.length) {
      navigate(chooseAccountPath);
    } else {
      navigate(userLoginPath);
    }
  };

  const handleViewerProfile = () => {
    setMenuState(false);
    navigate(accountSettingDetailsUrl(user.id));
  };

  const handleBusinessDetails = () => {
    setMenuState(false);
    navigate(userBusinessDetailsPath);
  };

  const handleMenuItemClick = (url: string, event: React.MouseEvent<any>) => {
    event.stopPropagation();
    event.preventDefault();
    setDrawerState(false);
    navigate(url);
  };

  const handleIsMenuSmall = () => {
    setMenuSmall(!isMenuSmall);
  };

  const handleErrorBack = () => {
    navigate("/");
    dispatchAppState({
      payload: {
        error: null
      },
      type: "displayError"
    });
  };
  const [isUpdate, setUpdate] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showToggleButton, setShowToggleButton] = useState(false);
  const hasSellerMaster = user.permissionGroups.filter(
    permission => permission.smopUsertypeCode === "SELLER_MASTER"
  );
  useEffect(() => {
    if (
      (user.accessGroup == null && hasSellerMaster.length) ||
      (user.businessDetails && user.businessDetails.status === "RVW")
    ) {
      setShowMenu(true);
      navigate("/business-details");
    }
    return () => {
      setUpdate(false);
    };
  }, [isUpdate]);

  return (
    <AppHeaderContext.Provider value={appHeaderAnchor}>
      <AppActionContext.Provider
        value={{
          anchor: appActionAnchor,
          docked,
          setDocked
        }}
      >
        <div className={classes.root}>
          <div
            className={classes.sideBar}
            onMouseEnter={() => setShowToggleButton(true)}
            onMouseLeave={() => setShowToggleButton(false)}
          >
            {!showMenu && (
              <ResponsiveDrawer
                onClose={() => setDrawerState(false)}
                open={isDrawerOpened}
                small={!isMenuSmall}
                isMfaSetupLater={
                  user?.mfaRemainingGraceDays !== 0 &&
                  user.permissionGroups.some(
                    permission =>
                      permission.mfaEnabledDate !== null &&
                      ["OMI Admin", "Ops Master", "OMI Ops"].includes(
                        permission.name
                      )
                  )
                    ? true
                    : false
                }
              >
                <Hidden smDown>
                  <div
                    className={classNames(classes.isMenuSmall, {
                      [classes.isMenuSmallHide]: isMenuSmall,
                      [classes.isMenuSmallDark]: isDark,
                      [classes.isToggleButtonHidden]: !showToggleButton
                    })}
                    onClick={handleIsMenuSmall}
                  >
                    <SVG src={menuArrowIcon} />
                  </div>
                </Hidden>

                <MenuList
                  className={isMenuSmall ? classes.menuSmall : classes.menu}
                  menuItems={menuStructure}
                  isMenuSmall={!isMenuSmall}
                  location={location.pathname}
                  user={user}
                  renderConfigure={renderConfigure}
                  onMenuItemClick={handleMenuItemClick}
                />
              </ResponsiveDrawer>
            )}
          </div>
          <div
            className={classNames(classes.content, {
              [classes.contentToggle]: isMenuSmall
            })}
          >
            {appState.loading ? (
              <LinearProgress className={classes.appLoader} color="primary" />
            ) : (
              <div className={classes.appLoaderPlaceholder} />
            )}
            <div className={classes.viewContainer}>
              <div>
                <Container>
                  <div className={classes.header}>
                    {!showMenu && (
                      <div
                        className={classNames(classes.menuIcon, {
                          [classes.menuIconOpen]: isDrawerOpened,
                          [classes.menuIconDark]: isDark
                        })}
                        onClick={() => setDrawerState(!isDrawerOpened)}
                      >
                        <span />
                        <span />
                        <span />
                        <span />
                      </div>
                    )}
                    <SaveAccountDialog user={user} />
                    <div ref={appHeaderAnchor} />
                    <div className={classes.spacer} />
                    <div className={classes.userBar}>
                      <div className={classes.userMenuContainer} ref={anchor}>
                        <Chip
                          avatar={
                            user.avatar && (
                              <Avatar alt="user" src={user.avatar.url} />
                            )
                          }
                          classes={{
                            avatar: classes.avatar,
                            label: classes.userChipLabel
                          }}
                          className={classes.userChip}
                          label={
                            <>
                              {user.email}
                              <ArrowDropdown
                                className={classNames(classes.arrow, {
                                  [classes.rotate]: isMenuOpened
                                })}
                              />
                            </>
                          }
                          onClick={() => setMenuState(!isMenuOpened)}
                          data-test="userMenu"
                        />
                        <Popper
                          className={classes.popover}
                          open={isMenuOpened}
                          anchorEl={anchor.current}
                          transition
                          placement="bottom-end"
                        >
                          {({ TransitionProps, placement }) => (
                            <Grow
                              {...TransitionProps}
                              style={{
                                transformOrigin:
                                  placement === "bottom"
                                    ? "right top"
                                    : "right bottom"
                              }}
                            >
                              <Paper>
                                <AccountMenu
                                  user={user}
                                  useStyles={useStyles}
                                  setMenuState={setMenuState}
                                  showMenu={showMenu}
                                  handleViewerProfile={handleViewerProfile}
                                  hasSellerMaster={hasSellerMaster}
                                  handleBusinessDetails={handleBusinessDetails}
                                  handleLogout={handleLogout}
                                />
                              </Paper>
                            </Grow>
                          )}
                        </Popper>
                      </div>
                    </div>
                  </div>
                </Container>
              </div>
              <main className={classes.view}>
                {appState.error
                  ? appState.error === "unhandled" && (
                      <ErrorPage onBack={handleErrorBack} />
                    )
                  : children}
              </main>
            </div>
            <div
              className={classNames(classes.appAction, {
                [classes.appActionDocked]: docked
              })}
              ref={appActionAnchor}
            />
          </div>
        </div>
      </AppActionContext.Provider>
    </AppHeaderContext.Provider>
  );
};

export default AppLayout;
